import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { Printer } from 'lucide-react';

import { Button } from '../components/ui';
import { DataTable, type ColumnDef } from '../components/DataTable';
import { apiService } from '../services/apiService';
import type { FulfillmentItem } from '../services/types';
import { Breadcrumb } from '../components/Breadcrumb';
import {
  getCachedData,
  setCachedData,
  clearAllCachedDataFor,
} from '../utils/pageCache';

/**
 * Helper function to format price with thousand separator
 * Converts $12345.67 to $12 345.67
 */
function formatPrice(value: number): string {
  const formatted = value.toFixed(2);
  const [dollars, cents] = formatted.split('.');
  const dollarsParts: string[] = [];
  let count = 0;
  for (let i = dollars.length - 1; i >= 0; i--) {
    if (count === 3) {
      dollarsParts.unshift(' ');
      count = 0;
    }
    dollarsParts.unshift(dollars[i]);
    count++;
  }
  return `$${dollarsParts.join('')}.${cents}`;
}

type SortBy =
  | 'calculatedDemand'
  | 'customerLocation'
  | 'partNumber'
  | 'requestDate'
  | 'totalPrice'
  | 'fulfillmentPercentage'
  | 'orderedQuantity'
  | 'shippedQuantity'
  | 'onHandQuantity'
  | 'orderAge'
  | 'daysLate';
type SortOrder = 'asc' | 'desc';

const validSortByValues: SortBy[] = [
  'calculatedDemand',
  'customerLocation',
  'partNumber',
  'requestDate',
  'totalPrice',
  'fulfillmentPercentage',
  'orderedQuantity',
  'shippedQuantity',
  'onHandQuantity',
  'orderAge',
  'daysLate',
];

export const FulfillmentPage: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  // Read initial state from URL params
  const urlPage = parseInt(searchParams.get('page') || '1', 10);
  const urlSearch = searchParams.get('search') || '';
  const urlSortBy = searchParams.get('sortBy') as SortBy | null;
  const urlSortOrder = searchParams.get('sortOrder') as SortOrder | null;

  const limit = 50;
  const offset = (urlPage - 1) * limit;
  const searchTerm = urlSearch;
  const sortBy: SortBy = validSortByValues.includes(urlSortBy as SortBy)
    ? (urlSortBy as SortBy)
    : 'calculatedDemand';
  const sortOrder: SortOrder =
    urlSortOrder === 'asc' || urlSortOrder === 'desc' ? urlSortOrder : 'asc';

  const [items, setItems] = useState<FulfillmentItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [totalBacklog, setTotalBacklog] = useState(0);
  const [inputValue, setInputValue] = useState(urlSearch);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Update URL params helper
  const updateUrlParams = useCallback(
    (updates: {
      page?: number;
      search?: string;
      sortBy?: SortBy;
      sortOrder?: SortOrder;
    }) => {
      const newParams = new URLSearchParams(searchParams);

      if (updates.page !== undefined) {
        if (updates.page > 1) {
          newParams.set('page', updates.page.toString());
        } else {
          newParams.delete('page');
        }
      }
      if (updates.search !== undefined) {
        if (updates.search) {
          newParams.set('search', updates.search);
        } else {
          newParams.delete('search');
        }
      }
      if (updates.sortBy !== undefined) {
        if (updates.sortBy !== 'calculatedDemand') {
          newParams.set('sortBy', updates.sortBy);
        } else {
          newParams.delete('sortBy');
        }
      }
      if (updates.sortOrder !== undefined) {
        if (updates.sortOrder !== 'asc') {
          newParams.set('sortOrder', updates.sortOrder);
        } else {
          newParams.delete('sortOrder');
        }
      }

      setSearchParams(newParams, { replace: false });
    },
    [searchParams, setSearchParams]
  );

  // Cache key based on search params
  const cacheKey = `fulfillment_${offset}_${sortBy}_${sortOrder}_${searchTerm}`;

  // Fetch fulfillment items when URL params change
  useEffect(() => {
    const fetchFulfillmentItems = async () => {
      // Check cache first
      const cached = getCachedData<{
        items: FulfillmentItem[];
        total: number;
        totalBacklog: number;
      }>(cacheKey);

      if (cached) {
        setItems(cached.items);
        setTotal(cached.total);
        setTotalBacklog(cached.totalBacklog);
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        const response = await apiService.getFulfillmentItems(
          offset,
          limit,
          sortBy,
          sortOrder,
          searchTerm || undefined
        );
        setItems(response.items);
        setTotal(response.pagination.total);
        setTotalBacklog(response.pagination.totalBacklog);

        // Cache the result
        setCachedData(cacheKey, {
          items: response.items,
          total: response.pagination.total,
          totalBacklog: response.pagination.totalBacklog,
        });
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : 'Failed to fetch fulfillment items'
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchFulfillmentItems();
  }, [offset, searchTerm, sortBy, sortOrder, cacheKey]);

  // Keep search input focused when user is actively searching
  useEffect(() => {
    if (searchTerm && !isLoading && searchInputRef.current) {
      if (document.activeElement !== searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }
  }, [searchTerm, isLoading]);

  // Sync input value with URL search param
  useEffect(() => {
    setInputValue(urlSearch);
  }, [urlSearch]);

  const handlePreviousPage = () => {
    const currentPage = Math.floor(offset / limit) + 1;
    if (currentPage > 1) {
      updateUrlParams({ page: currentPage - 1 });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleNextPage = () => {
    const currentPage = Math.floor(offset / limit) + 1;
    if (offset + limit < total) {
      updateUrlParams({ page: currentPage + 1 });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSort = (column: SortBy) => {
    // If clicking the same column, toggle the sort order
    if (sortBy === column) {
      updateUrlParams({
        sortOrder: sortOrder === 'asc' ? 'desc' : 'asc',
        page: 1,
      });
    } else {
      // If clicking a different column, sort by that column in ascending order
      updateUrlParams({ sortBy: column, sortOrder: 'asc', page: 1 });
    }
  };

  const handleSearchChange = (value: string) => {
    setInputValue(value);
    // Clear cache to force fresh data on new search
    clearAllCachedDataFor('fulfillment');
    // Debounce the URL update
    updateUrlParams({ search: value, page: 1 });
  };

  const getSortIndicator = (column: SortBy) => {
    if (sortBy !== column) return '';
    return sortOrder === 'asc' ? ' ↑' : ' ↓';
  };

  const currentPage = Math.floor(offset / limit) + 1;
  const totalPages = Math.ceil(total / limit);
  const hasMorePages = offset + limit < total;

  const columns: ColumnDef<FulfillmentItem>[] = [
    {
      key: 'calculatedDemand',
      header: `Demand${getSortIndicator('calculatedDemand')}`,
      sortable: true,
      render: (value) => {
        // Extract order number from calculatedDemand (formats: "111385 / 1" or "200121-1")
        const demandStr = value as string;
        // Handle both " / " and "-" separators
        const orderNum = demandStr.includes(' / ')
          ? demandStr.split(' / ')[0]
          : demandStr.split('-')[0];
        return (
          <Link
            to={`/sales-orders/${encodeURIComponent(orderNum)}`}
            className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
          >
            {demandStr}
          </Link>
        );
      },
    },
    {
      key: 'customerLocation',
      header: `Customer${getSortIndicator('customerLocation')}`,
      sortable: true,
      render: (value: unknown, item: unknown) => {
        const customerName = (value as string) || '—';
        const fulfillmentItem = item as FulfillmentItem;
        const onCreditHold = fulfillmentItem.creditHold;

        return (
          <div className="flex flex-col items-start gap-1">
            <span>{customerName}</span>
            {onCreditHold && (
              <span
                className="inline-block px-2 py-0.5 text-xs font-semibold text-red-800 bg-red-100 rounded cursor-help"
                title={
                  fulfillmentItem.creditHoldReason ||
                  'Customer is on credit hold'
                }
              >
                Credit Hold
              </span>
            )}
          </div>
        );
      },
    },
    {
      key: 'part',
      header: `Part${getSortIndicator('partNumber')}`,
      sortable: true,
      render: (value: unknown, item: unknown) => {
        if (!value) return '—';
        const fulfillmentItem = item as FulfillmentItem;
        const erpPartNum = fulfillmentItem.erpPartNum;
        const hasCustomerReference =
          fulfillmentItem.customerPartNumber !== null;

        return (
          <div className="flex flex-col items-start gap-1">
            <Link
              to={`/parts/${encodeURIComponent(erpPartNum)}`}
              className="text-blue-600 hover:text-blue-800 hover:underline"
            >
              {value as string} ({erpPartNum})
            </Link>
            {hasCustomerReference && (
              <div className="flex items-center gap-1">
                <span
                  className="text-red-500 font-bold text-lg leading-none"
                  title={`Customer Reference: ${fulfillmentItem.customerPartNumber}`}
                  aria-label={`Customer reference indicator: ${fulfillmentItem.customerPartNumber}`}
                >
                  ⚠
                </span>
                <span className="text-sm text-gray-600">
                  Cust: {fulfillmentItem.customerPartNumber}
                </span>
              </div>
            )}
          </div>
        );
      },
    },
    {
      key: 'partDescription',
      header: 'Description',
      className: 'max-w-xs truncate',
    },
    {
      key: 'fulfillmentPercentage',
      header: `Fulfilment %${getSortIndicator('fulfillmentPercentage')}`,
      align: 'right',
      sortable: true,
      render: (value) => `${Math.round(value as number)}%`,
    },
    {
      key: 'orderedQuantity',
      header: `Ordered Qty${getSortIndicator('orderedQuantity')}`,
      align: 'right',
      sortable: true,
      render: (value) => Math.round(value as number).toString(),
    },
    {
      key: 'shippedQuantity',
      header: `Shipped Qty${getSortIndicator('shippedQuantity')}`,
      align: 'right',
      sortable: true,
      render: (value) => {
        const qty = value === null ? 0 : (value as number);
        return Math.round(qty).toString();
      },
    },
    {
      key: 'onHandQuantity',
      header: `On Hand Qty${getSortIndicator('onHandQuantity')}`,
      align: 'right',
      sortable: true,
      render: (value) => {
        const qty = value === null ? 0 : (value as number);
        return Math.round(qty).toString();
      },
    },
    {
      key: 'orderAge',
      header: `${t('navigation.fulfillment.age')}${getSortIndicator('orderAge')}`,
      align: 'right',
      sortable: true,
      render: (value) => {
        if (value === null) return '—';
        return `${value}d`;
      },
    },
    {
      key: 'daysLate',
      header: `${t('navigation.fulfillment.daysLate')}${getSortIndicator('daysLate')}`,
      align: 'right',
      sortable: true,
      render: (value) => {
        if (value === null) return '—';
        const days = value as number;
        const color = days > 0 ? 'text-red-600' : 'text-green-600';
        return <span className={`font-medium ${color}`}>{days}d</span>;
      },
    },
    {
      key: 'totalPriceCAD',
      header: `Remaining Price (CAD)${getSortIndicator('totalPrice')}`,
      align: 'right',
      render: (value) => formatPrice(value as number),
      sortable: true,
    },
    {
      key: '_action',
      header: t('parts.search.action'),
      render: (_, item) => {
        const fulfillmentItem = item as FulfillmentItem;
        const part = fulfillmentItem.customerPartNumber || fulfillmentItem.part;
        return (
          <Link
            to={`/label-printing?type=external-part&part=${encodeURIComponent(part)}`}
            className="inline-flex items-center justify-center gap-1 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 h-9 px-3 bg-green-600 text-white hover:bg-green-700"
            title="Print label for this part"
          >
            <Printer className="w-4 h-4" />
            {t('labelPrinting.print')}
          </Link>
        );
      },
    },
  ];

  const Pagination = () => (
    <div className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg flex items-center justify-between text-sm">
      <div className="text-gray-600">
        Showing {offset + 1} to {Math.min(offset + limit, total)} of {total}{' '}
        items
      </div>
      <div className="flex gap-2">
        <Button
          onClick={handlePreviousPage}
          disabled={offset === 0}
          variant="outline"
          size="sm"
        >
          Previous
        </Button>
        <div className="flex items-center gap-2 px-3 text-gray-600">
          Page {currentPage} of {totalPages}
        </div>
        <Button
          onClick={handleNextPage}
          disabled={!hasMorePages}
          variant="outline"
          size="sm"
        >
          Next
        </Button>
      </div>
    </div>
  );

  return (
    <div className="p-4 sm:p-6 lg:p-8 w-full">
      <div className="w-full">
        <Breadcrumb currentPage={t('navigation.fulfillment')} />

        {/* Header */}
        <div className="mb-6 mt-4">
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight mb-2">
            {t('navigation.fulfillment') || 'Fulfillment'}
          </h1>
          <p className="text-muted-foreground">
            {t('navigation.fulfillment.description') ||
              'Track order fulfillment status and inventory'}
          </p>
          {totalBacklog > 0 && (
            <p className="text-lg font-semibold text-gray-900 mt-2">
              {t('navigation.fulfillment.totalBacklog')}:{' '}
              {formatPrice(totalBacklog)} CAD
            </p>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800">
            <p className="font-semibold">Error loading fulfillment data</p>
            <p className="text-sm">{error}</p>
          </div>
        )}

        {/* Search Box */}
        <div className="mb-6 relative z-10">
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Search by Demand, Customer, Part, or Description..."
            value={inputValue}
            onChange={(e) => handleSearchChange(e.target.value)}
            disabled={isLoading && items.length === 0}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white disabled:bg-gray-100 disabled:opacity-75"
          />
        </div>

        {/* Top Pagination */}
        {total > 0 && <Pagination />}

        {/* Table Container with Loading Overlay */}
        <div className="relative">
          <div className="mt-4 w-full">
            {isLoading && items.length === 0 && (
              <div className="flex justify-center items-center py-12">
                <div className="flex flex-col items-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4" />
                  <p className="text-gray-600">Loading fulfillment items...</p>
                </div>
              </div>
            )}
            {!isLoading || items.length > 0 ? (
              <>
                <DataTable
                  columns={columns}
                  data={items}
                  keyExtractor={(item) => item.id}
                  isEmpty={items.length === 0}
                  emptyMessage="No fulfillment items found"
                  onColumnSort={(columnKey) => {
                    // Map column keys to sort field names
                    const sortFieldMap: Record<string, SortBy> = {
                      calculatedDemand: 'calculatedDemand',
                      customerLocation: 'customerLocation',
                      part: 'partNumber',
                      totalPriceCAD: 'totalPrice',
                      fulfillmentPercentage: 'fulfillmentPercentage',
                      orderedQuantity: 'orderedQuantity',
                      shippedQuantity: 'shippedQuantity',
                      onHandQuantity: 'onHandQuantity',
                      orderAge: 'orderAge',
                      daysLate: 'daysLate',
                    };
                    const sortBy = sortFieldMap[columnKey];
                    if (sortBy) {
                      handleSort(sortBy);
                    }
                  }}
                />

                {/* Bottom Pagination */}
                {total > 0 && (
                  <div className="mt-4">
                    <Pagination />
                  </div>
                )}
              </>
            ) : null}
          </div>

          {/* Loading Overlay on Refresh */}
          {isLoading && items.length > 0 && (
            <div className="absolute inset-0 bg-white/50 backdrop-blur-sm rounded-lg flex items-center justify-center">
              <div className="flex flex-col items-center">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mb-3" />
                <p className="text-gray-600 text-sm">Loading...</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
